import { type AuthUser } from "wasp/auth";
import DefaultLayout from "../layout/DefaultLayout";
import ProductsEmptyView from "../../views/ProductsFirstTimeView";
import ProductListView from "../../views/ProductsView/ProductsView";
import { useEffect, useState } from "react";
import setDocumentTitle from "../../setDocumentTitle";
import type { RootState } from "../../store/store";
import { useSelector, useDispatch } from "react-redux";
import { useApi, ApiProvider } from "../../hooks/useApi";
import { updateEnrichmentTask2 } from "wasp/client/operations";
import { setFetchingProducts, setLastFilteredProductCount } from "../../store/flowSlice";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { AlertCircle, X } from "lucide-react";
import { setEnrichment } from "../../store/score/scoreSlice";
import { useTranslation } from "../../i18n/hooks";

const AdminProductsPage = ({ user }: { user: AuthUser }) => {
  const dispatch = useDispatch();
  const api = useApi();
  const { t } = useTranslation();
  const { currentCollection } = useSelector((state: RootState) => state.user);
  const {
    searchKeyword,
    selectedTags,
    date,
    sortBy,
    currentPage,
    qualityGroup,
    categoryHierarchy,
    categoryName,
    productType,
  } = useSelector((state: RootState) => state.filters);
  const { lastUnsyncedJob } = useSelector((state: RootState) => state.flow);

  const hasProducts =
    currentCollection?.collection?.miqHasCompletedShopifyImport;
  const ITEMS_PER_PAGE = 10;
  const offset = (currentPage - 1) * ITEMS_PER_PAGE;

  const {
    data: productsData,
    isLoading: isFetching,
    isRefetching,
    error,
  } = api.useProducts({
    accountId: user.email ?? "",
    collectionName: currentCollection?.collection?.name ?? "",
    limit: ITEMS_PER_PAGE,
    offset: offset,
    sortField: sortBy ?? "",
    tableId: currentCollection?.collection?.tableId ?? "",
    filters: {
      quality_group: qualityGroup,
      tags: selectedTags,
      search: searchKeyword,
      date_range: date,
      category_hierarchy: categoryHierarchy,
      category_name: categoryName,
      product_type: productType,
    },
    metaFields: [],
  });

  useEffect(() => {
    if (productsData?.data?.products) {
      dispatch(
        setEnrichment({
          enrichmentJobId: "base",
          products: productsData?.data?.products,
        })
      );
      dispatch(setLastFilteredProductCount(productsData?.data?.products?.length ?? 0));
    }
  }, [productsData?.data?.products]);

  useEffect(() => {
    setDocumentTitle(t('sidebar.products'));
  }, []);

  useEffect(() => {
    dispatch(setFetchingProducts(isFetching));
  }, [isFetching])

  const [showUnsyncedAlert, setShowUnsyncedAlert] = useState(true);

  const handleDismissAlert = async () => {
    if (!lastUnsyncedJob) return;
    await updateEnrichmentTask2({
      id: lastUnsyncedJob?.id,
      syncMessageDismissed: true,
    });
    setShowUnsyncedAlert(false);
  };

  return (
    <DefaultLayout user={user}>
      {hasProducts ? (
        <ProductListView
          products={productsData?.data?.products ?? []}
          isFetching={isFetching}
          isRefetching={false}
          error={productsData?.error?.message ?? null}
          summary={productsData?.data?.content_quality_overview}
        />
      ) : (
        <ProductsEmptyView user={user} />
      )}
      <></>
    </DefaultLayout>
  );
};

export default AdminProductsPage;
